import { AVATAR_LABELS, MESSAGE_ROLES, UI_STATES } from '@/components/SearchAssistant/constants';
import { Box, Typography } from '@mui/material';

import { AssistantAvatar } from '@/components/SearchAssistant/components/AssistantAvatar';
import { formatRelativeDate } from '@/utils/dateFormatter';
import { MuiMarkdown } from 'mui-markdown';

const styles = {
  messageContainer: {
    display: 'flex',
    gap: (theme) => theme.spacing(1.25),
    padding: (theme) => theme.spacing(1, 2),
    scrollSnapAlign: 'end',
  },
  assistantContainer: {
    justifyContent: 'flex-start',
  },
  userContainer: {
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
  },
  messageContent: {
    display: 'grid',
    maxWidth: 'clamp(100px, 80%, 22.5rem)',
  },
  userName: {
    fontSize: '0.750rem',
    lineHeight: 1.25,
    paddingBottom: (theme) => theme.spacing(0.5),
  },
  userUserName: {
    textAlign: 'right',
  },
  messageBubble: {
    padding: (theme) => theme.spacing(1, 1.75),
    borderRadius: (theme) => theme.spacing(1),
    wordWrap: 'break-word',
  },
  assistantBubble: {
    marginTop: (theme) => theme.spacing(1.5),
    color: (theme) => `${theme.palette.baseColors.deepSeaNavy.c100}`,
    backgroundColor: (theme) => `${theme.palette.baseColors.skyBlue.c100}50`,
    borderTopLeftRadius: 0,
  },
  userBubble: {
    backgroundColor: (theme) => theme.palette.baseColors.offWhite.c100,
    color: (theme) => `${theme.palette.baseColors.deepSeaNavy.c100}`,
    borderTopRightRadius: 0,
  },
  pendingBubble: {
    opacity: 0.7,
    backgroundColor: '#E3F2FD',
    color: '#1976D2',
    borderTopRightRadius: 0,
  },
  messageText: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: 'inherit',

    a: {
      color: (theme) => theme.palette.baseColors.fireOrange.c100,
    },

    'td.MuiTableCell-root': {
      whiteSpace: 'normal',
    },
  },
  timestamp: {
    alignItems: 'center',
    display: 'inline-flex',
  },
  assistantTimestamp: {
    justifyContent: 'flex-start',
  },
  userTimestamp: {
    justifyContent: 'flex-end',
  },
  timestampText: {
    color: (theme) => theme.palette.grey[500],
    fontSize: '0.6rem',
    fontWeight: 400,
    lineHeight: 1.33,
    padding: (theme) => theme.spacing(0.5, 0),
  },
};

const MessageTimestamp = ({ children, sx = {} }) => {
  return (
    <Box sx={{ ...styles.timestamp, ...sx }}>
      <Typography variant="caption" sx={styles.timestampText}>
        {children}
      </Typography>
    </Box>
  );
};

const MessageContainer = ({ children, sx = {} }) => {
  return <Box sx={{ ...styles.messageContainer, ...sx }}>{children}</Box>;
};

const MessageContent = ({ children, sx = {} }) => {
  return <Box sx={{ ...styles.messageContent, ...sx }}>{children}</Box>;
};

const MessageBubble = ({ children, sx = {} }) => {
  return <Box sx={{ ...styles.messageBubble, ...sx }}>{children}</Box>;
};

export const AssistantMessageContainer = ({ children, createdAt = null }) => {
  return (
    <MessageContainer sx={styles.assistantContainer}>
      <AssistantAvatar />
      <MessageContent sx={styles.messageContent}>
        <MessageBubble sx={styles.assistantBubble}>{children}</MessageBubble>
        {createdAt ? (
          <MessageTimestamp sx={styles.assistantTimestamp}>
            {formatRelativeDate(new Date(createdAt))}
          </MessageTimestamp>
        ) : null}
      </MessageContent>
    </MessageContainer>
  );
};

const AssistantMessage = ({ content, createdAt }) => {
  return (
    <AssistantMessageContainer createdAt={createdAt}>
      <Typography sx={styles.messageText}>
        <MuiMarkdown>{content}</MuiMarkdown>
      </Typography>
    </AssistantMessageContainer>
  );
};

const UserMessage = ({ content, status, createdAt }) => {
  const isPending = status === UI_STATES.PENDING;

  return (
    <MessageContainer sx={styles.userContainer}>
      <MessageContent sx={styles.messageContent}>
        <Typography variant="caption" sx={{ ...styles.userName, ...styles.userUserName }}>
          {AVATAR_LABELS.USER_FULL}
        </Typography>
        <MessageBubble sx={isPending ? styles.pendingBubble : styles.userBubble}>
          <Typography sx={styles.messageText}>{content}</Typography>
        </MessageBubble>
        <MessageTimestamp sx={styles.userTimestamp}>
          {isPending ? 'Sending...' : formatRelativeDate(new Date(createdAt))}
        </MessageTimestamp>
      </MessageContent>
    </MessageContainer>
  );
};

export const Message = ({ role, content, status, createdAt }) => {
  if (!content || content.length === 0) {
    return null;
  }

  if (role === MESSAGE_ROLES.ASSISTANT) {
    return <AssistantMessage content={content} createdAt={createdAt} />;
  }

  return <UserMessage content={content} status={status} createdAt={createdAt} />;
};
